import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const TIPOS_ENTRADA = ['entrada', 'entrada_desplazamiento'];

const TIPO_LABEL = {
  entrada:                { txt: 'Entrada',              cls: 'bg-green-100 text-green-800' },
  entrada_desplazamiento: { txt: '🚗 Ent. Desplaz.',    cls: 'bg-teal-100 text-teal-800' },
  salida:                 { txt: 'Salida',               cls: 'bg-red-100 text-red-800' },
  inicio_pausa:           { txt: 'Ini. Pausa',           cls: 'bg-yellow-100 text-yellow-800' },
  fin_pausa:              { txt: 'Fin Pausa',            cls: 'bg-blue-100 text-blue-800' },
};

function calcHoras(fichajes) {
  let total = 0, entrada = null, pausaIni = null, pausaAcum = 0;
  for (const f of [...fichajes].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))) {
    const ts = new Date(f.timestamp).getTime();
    if (TIPOS_ENTRADA.includes(f.tipo)) { entrada = ts; pausaAcum = 0; pausaIni = null; }
    else if (f.tipo === 'inicio_pausa' && entrada) pausaIni = ts;
    else if (f.tipo === 'fin_pausa' && pausaIni) { pausaAcum += ts - pausaIni; pausaIni = null; }
    else if (f.tipo === 'salida' && entrada) { total += (ts - entrada) - pausaAcum; entrada = null; }
  }
  if (entrada) {
    const now = Date.now();
    total += (now - entrada) - pausaAcum - (pausaIni ? now - pausaIni : 0);
  }
  const h = Math.floor(total / 3600000);
  const m = Math.round((total % 3600000) / 60000);
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

function toLocalInput(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmtTime(ts) {
  return new Date(ts).toLocaleTimeString('es-ES', { timeZone: 'Europe/Madrid', hour: '2-digit', minute: '2-digit' });
}
function fmtDate(ts) {
  return new Date(ts).toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid', weekday: 'short', day: '2-digit', month: '2-digit' });
}

export default function EditarFichajes({ trabajadorInicial }) {
  const now = new Date();
  const [trabajadores, setTrabajadores] = useState([]);
  const [selectedId, setSelectedId] = useState(trabajadorInicial?.id || '');
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [anio, setAnio] = useState(now.getFullYear());
  const [fichajes, setFichajes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editando, setEditando] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [nuevoForm, setNuevoForm] = useState(null);

  useEffect(() => {
    api.get('/trabajadores').then(r => setTrabajadores(r.data.trabajadores));
  }, []);

  const fetchFichajes = useCallback(async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      const res = await api.get(`/admin/fichajes/${selectedId}?mes=${mes}&anio=${anio}`);
      setFichajes(res.data.fichajes);
    } catch { setFichajes([]); }
    finally { setLoading(false); }
  }, [selectedId, mes, anio]);

  useEffect(() => { fetchFichajes(); }, [fetchFichajes]);

  const showMsg = (tipo, texto) => {
    setMsg({ tipo, texto });
    setTimeout(() => setMsg(null), 4000);
  };

  const openEdit = (f) => {
    setEditando(f.id);
    setEditForm({ timestamp: toLocalInput(f.timestamp), tipo: f.tipo });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const ts = new Date(editForm.timestamp).toISOString();
      await api.put(`/admin/fichajes/${editando}`, { timestamp: ts, tipo: editForm.tipo });
      showMsg('ok', 'Fichaje actualizado. Cambio auditado.');
      setEditando(null);
      fetchFichajes();
    } catch (err) {
      showMsg('err', err.response?.data?.error || 'Error al actualizar');
    } finally { setSaving(false); }
  };

  const deleteFichaje = async (id) => {
    if (!window.confirm('¿Eliminar este fichaje? La acción quedará registrada en la auditoría.')) return;
    try {
      await api.delete(`/admin/fichajes/${id}`);
      showMsg('ok', 'Fichaje eliminado. Cambio auditado.');
      fetchFichajes();
    } catch (err) {
      showMsg('err', err.response?.data?.error || 'Error al eliminar');
    }
  };

  const saveNuevo = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const ts = new Date(nuevoForm.timestamp).toISOString();
      await api.post('/admin/fichajes', { usuario_id: selectedId, tipo: nuevoForm.tipo, timestamp: ts });
      showMsg('ok', 'Fichaje creado. Cambio auditado.');
      setNuevoForm(null);
      fetchFichajes();
    } catch (err) {
      showMsg('err', err.response?.data?.error || 'Error al crear');
    } finally { setSaving(false); }
  };

  const porDia = {};
  for (const f of fichajes) {
    const dia = new Date(f.timestamp).toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit' });
    if (!porDia[dia]) porDia[dia] = [];
    porDia[dia].push(f);
  }
  const dias = Object.entries(porDia).sort((a, b) => b[0].localeCompare(a[0]));

  const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="card space-y-3">
        <div>
          <label className="label">Trabajador</label>
          <select className="input" value={selectedId} onChange={e => setSelectedId(e.target.value)}>
            <option value="">— Seleccionar trabajador —</option>
            {trabajadores.filter(t => t.rol === 'trabajador').map(t => (
              <option key={t.id} value={t.id}>{t.apellidos}, {t.nombre}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="label">Mes</label>
            <select className="input" value={mes} onChange={e => setMes(Number(e.target.value))}>
              {meses.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div className="w-28">
            <label className="label">Año</label>
            <select className="input" value={anio} onChange={e => setAnio(Number(e.target.value))}>
              {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        {selectedId && (
          <button onClick={() => setNuevoForm({ tipo: 'entrada', timestamp: new Date().toISOString().slice(0, 16) })}
            className="btn-primary text-sm w-full">
            + Añadir fichaje manual
          </button>
        )}
      </div>

      {/* Mensaje */}
      {msg && (
        <div className={`px-4 py-3 rounded-xl text-sm font-medium
          ${msg.tipo === 'ok' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {msg.texto}
        </div>
      )}

      {/* Modal nuevo fichaje */}
      {nuevoForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="font-bold text-gray-800 mb-4">Nuevo Fichaje Manual</h3>
            <form onSubmit={saveNuevo} className="space-y-3">
              <div>
                <label className="label">Tipo</label>
                <select className="input" value={nuevoForm.tipo}
                  onChange={e => setNuevoForm(f => ({ ...f, tipo: e.target.value }))}>
                  <option value="entrada">Entrada</option>
                  <option value="entrada_desplazamiento">Entrada (Desplazamiento)</option>
                  <option value="salida">Salida</option>
                  <option value="inicio_pausa">Inicio Pausa</option>
                  <option value="fin_pausa">Fin Pausa</option>
                </select>
              </div>
              <div>
                <label className="label">Fecha y hora</label>
                <input type="datetime-local" className="input" required
                  value={nuevoForm.timestamp}
                  onChange={e => setNuevoForm(f => ({ ...f, timestamp: e.target.value }))} />
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setNuevoForm(null)} className="btn-secondary flex-1">Cancelar</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Fichajes */}
      {!selectedId ? (
        <div className="card text-center py-8 text-gray-400">Selecciona un trabajador para ver sus fichajes</div>
      ) : loading ? (
        <div className="text-center py-8 text-gray-400">Cargando fichajes...</div>
      ) : dias.length === 0 ? (
        <div className="card text-center py-8 text-gray-400">
          <div className="text-3xl mb-2">📋</div>
          No hay fichajes para este período
        </div>
      ) : (
        <div className="space-y-3">
          {dias.map(([dia, fDia]) => (
            <div key={dia} className="card">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="font-semibold text-gray-800">{fDia[0] && fmtDate(fDia[0].timestamp)}</span>
                  <span className="ml-2 text-xs text-gray-400">{dia}</span>
                </div>
                <span className="text-sm font-bold text-mgh-blue-900">{calcHoras(fDia)}</span>
              </div>

              <div className="space-y-2">
                {fDia.map(f => {
                  const tl = TIPO_LABEL[f.tipo];
                  const isEditing = editando === f.id;

                  return (
                    <div key={f.id} className="border border-gray-100 rounded-xl p-3 bg-gray-50">
                      {isEditing ? (
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <select className="input flex-1 text-sm" value={editForm.tipo}
                              onChange={e => setEditForm(ef => ({ ...ef, tipo: e.target.value }))}>
                              <option value="entrada">Entrada</option>
                              <option value="entrada_desplazamiento">Entrada (Desplazamiento)</option>
                              <option value="salida">Salida</option>
                              <option value="inicio_pausa">Inicio Pausa</option>
                              <option value="fin_pausa">Fin Pausa</option>
                            </select>
                            <input type="datetime-local" className="input flex-1 text-sm"
                              value={editForm.timestamp}
                              onChange={e => setEditForm(ef => ({ ...ef, timestamp: e.target.value }))} />
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => setEditando(null)} className="btn-secondary text-xs flex-1 py-1.5">Cancelar</button>
                            <button onClick={saveEdit} disabled={saving} className="btn-primary text-xs flex-1 py-1.5">Guardar</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${tl.cls}`}>{tl.txt}</span>
                            <span className="font-mono text-sm">{fmtTime(f.timestamp)}</span>
                            {f.editado && <span className="text-xs text-amber-600 font-medium">✎ editado</span>}
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => openEdit(f)}
                              className="text-xs px-2.5 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">
                              Editar
                            </button>
                            <button onClick={() => deleteFichaje(f.id)}
                              className="text-xs px-2.5 py-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors">
                              Eliminar
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
